/**
 * Sueco cart gift accessory synchronizer.
 *
 * Main line items carry properties._gift_accessory_variant_ids.
 * Gift accessory lines are added by variant_id, then stay cart-editable.
 */
(function () {
  "use strict";

  if (window.__cartGiftSyncLoaded) return;
  window.__cartGiftSyncLoaded = true;

  var GIFT_PRODUCT_ID = 8251805696177;
  var GIFT_IDS_PROP = "_gift_accessory_variant_ids";
  var SYNC_HEADER = "X-Gift-Sync";
  var DEBOUNCE_MS = 500;
  var REMOVED_STORAGE_KEY = "sueco_removed_gift_accessory_variant_ids";
  var GIFT_ATTRIBUTE_KEYS = [
    "ギフトオプション",
    "熨斗",
    "熨斗の種類",
    "表書",
    "名前",
    "手提げ袋",
    "カード",
  ];

  var syncTimer = null;
  var syncing = false;
  var pendingReload = false;
  var lastCart = null;

  function scheduleSync(options) {
    pendingReload = pendingReload || !!(options && options.reload);
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var shouldReload = pendingReload;
      pendingReload = false;
      runSync({ reload: shouldReload });
    }, DEBOUNCE_MS);
  }

  function runSync(options) {
    if (syncing) return Promise.resolve();
    syncing = true;
    var shouldReload = !!(options && options.reload);

    return fetchCart()
      .then(function (cart) {
        lastCart = cart;
        return pruneRemovedGiftReferences(cart).then(function (pruned) {
          if (!pruned) return cart;
          return fetchCart().then(function (updatedCart) {
            lastCart = updatedCart;
            return updatedCart;
          });
        });
      })
      .then(function (cart) {
        var targets = collectTargets(cart);
        forgetPrunedGiftVariants(targets);
        markGiftAccessoryRows(cart);

        var gifts = cart.items.filter(function (item) {
          return item.product_id === GIFT_PRODUCT_ID;
        });

        var giftByVariantId = {};
        gifts.forEach(function (gift) {
          giftByVariantId[String(gift.variant_id)] = gift;
        });

        var updates = {};
        var addItems = [];
        var changed = false;
        var attributes = buildCartGiftAttributes(cart);
        var attributesChanged = haveAttributesChanged(
          cart.attributes || {},
          attributes,
        );

        gifts.forEach(function (gift) {
          var wanted = targets[String(gift.variant_id)] || 0;
          // 親行が無くなった付属品は落とす
          if (!wanted) {
            updates[gift.key] = 0;
            changed = true;
            return;
          }
          // 商品ページと同じく、付属品の数量は親行の数量に追従させる
          if (gift.quantity !== wanted) {
            updates[gift.key] = wanted;
            changed = true;
          }
        });

        Object.keys(targets).forEach(function (variantId) {
          if (
            targets[variantId] <= 0 ||
            giftByVariantId[variantId] ||
            isRemovedGiftVariant(variantId)
          )
            return;
          addItems.push({
            id: parseInt(variantId, 10),
            quantity: targets[variantId],
            properties: { _gift_accessory: "true" },
          });
          changed = true;
        });

        if (attributesChanged) changed = true;

        if (!changed) {
          syncing = false;
          return null;
        }

        var requests = [];
        var headers = { "Content-Type": "application/json" };
        headers[SYNC_HEADER] = "1";

        if (Object.keys(updates).length > 0 || attributesChanged) {
          var updatePayload = {};
          if (Object.keys(updates).length > 0) updatePayload.updates = updates;
          if (attributesChanged) updatePayload.attributes = attributes;
          requests.push(
            fetch("/cart/update.js", {
              method: "POST",
              credentials: "same-origin",
              headers: headers,
              body: JSON.stringify(updatePayload),
            }),
          );
        }

        if (addItems.length > 0) {
          requests.push(
            fetch("/cart/add.js", {
              method: "POST",
              credentials: "same-origin",
              headers: headers,
              body: JSON.stringify({ items: addItems }),
            }),
          );
        }

        return Promise.all(requests).then(function () {
          if (shouldReload && isCartPage()) window.location.reload();
        });
      })
      .catch(function (err) {
        console.error("[cart-gift-sync] error:", err);
      })
      .finally(function () {
        syncing = false;
      });
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" }).then(function (r) {
      return r.json();
    });
  }

  function collectTargets(cart) {
    var targets = {};
    cart.items.forEach(function (item) {
      if (item.product_id === GIFT_PRODUCT_ID) return;
      var ids = parseGiftIds((item.properties || {})[GIFT_IDS_PROP]);
      ids.forEach(function (id) {
        targets[id] = (targets[id] || 0) + item.quantity;
      });
    });
    return targets;
  }

  function pruneRemovedGiftReferences(cart) {
    var removedIds = readRemovedGiftVariants();
    if (removedIds.length === 0) return Promise.resolve(false);

    var requests = [];
    var headers = { "Content-Type": "application/json" };
    headers[SYNC_HEADER] = "1";

    cart.items.forEach(function (item) {
      if (item.product_id === GIFT_PRODUCT_ID) return;

      var properties = item.properties || {};
      var ids = parseGiftIds(properties[GIFT_IDS_PROP]);
      var affectedIds = ids.filter(function (id) {
        return removedIds.indexOf(id) !== -1;
      });
      if (affectedIds.length === 0) return;

      var remainingIds = ids.filter(function (id) {
        return removedIds.indexOf(id) === -1;
      });

      requests.push(
        fetch("/cart/change.js", {
          method: "POST",
          credentials: "same-origin",
          headers: headers,
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: pruneGiftProperties(
              properties,
              affectedIds,
              remainingIds,
            ),
          }),
        }),
      );
    });

    if (requests.length === 0) {
      forgetRemovedGiftVariants(removedIds);
      return Promise.resolve(false);
    }

    return Promise.all(requests).then(function () {
      return true;
    });
  }

  function pruneGiftProperties(properties, removedIds, remainingIds) {
    var next = {};
    Object.keys(properties || {}).forEach(function (key) {
      if (properties[key] !== "" && properties[key] != null) {
        next[key] = properties[key];
      }
    });

    var noshiVariantId = normalizeGiftId(properties["_noshi_variant_id"]);
    var cardVariantId = normalizeGiftId(properties["_card_variant_id"]);
    var removedBag = false;

    removedIds.forEach(function (id) {
      if (id === noshiVariantId) {
        delete next["のし"];
        delete next["_noshi_variant_id"];
        delete next["表書き"];
        delete next["名入れ"];
        delete next["のし配置"];
        return;
      }

      if (id === cardVariantId) {
        delete next["カード"];
        delete next["_card_variant_id"];
        delete next["カード文面"];
        return;
      }

      removedBag = true;
    });

    if (removedBag) {
      delete next["手提げ袋"];
      delete next["紙袋"];
      delete next["_bag_variant_id"];
    }

    if (remainingIds.length > 0) {
      next[GIFT_IDS_PROP] = JSON.stringify(remainingIds);
      next["_gift_options"] = "true";
    } else {
      [
        GIFT_IDS_PROP,
        "_gift_options",
        "のし",
        "_noshi_variant_id",
        "表書き",
        "名入れ",
        "のし配置",
        "手提げ袋",
        "紙袋",
        "_bag_variant_id",
        "カード",
        "_card_variant_id",
        "カード文面",
      ].forEach(function (key) {
        delete next[key];
      });
      if (Object.keys(next).length === 0) {
        next["_gift_options_removed"] = "true";
      }
    }

    return next;
  }

  function buildCartGiftAttributes(cart) {
    var giftQuantities = collectGiftQuantities(cart);
    var summaryParts = [];
    var legacyNoshi = {
      熨斗: "",
      熨斗の種類: "",
      表書: "",
      名前: "",
    };
    var firstBag = "";
    var firstCard = "";

    cart.items.forEach(function (item) {
      if (item.product_id === GIFT_PRODUCT_ID) return;
      var properties = item.properties || {};
      var ids = parseGiftIds(properties[GIFT_IDS_PROP]);
      if (ids.length === 0) return;

      var noshiVariantId = normalizeGiftId(properties["_noshi_variant_id"]);
      var cardVariantId = normalizeGiftId(properties["_card_variant_id"]);
      var bagVariantIds = ids.filter(function (id) {
        return id !== noshiVariantId && id !== cardVariantId;
      });

      if (
        properties["のし"] &&
        (!noshiVariantId || giftQuantities[noshiVariantId])
      ) {
        var noshiQuantitySuffix = giftQuantitySuffix(
          noshiVariantId ? [noshiVariantId] : ids,
          giftQuantities,
        );
        var noshi = "のし: " + properties["のし"];
        if (properties["表書き"]) noshi += " / 表書き: " + properties["表書き"];
        if (properties["名入れ"]) noshi += " / 名入れ: " + properties["名入れ"];
        summaryParts.push(
          [noshi, noshiQuantitySuffix].filter(Boolean).join(" "),
        );

        if (!legacyNoshi["熨斗の種類"]) {
          legacyNoshi.熨斗 = "有";
          legacyNoshi["熨斗の種類"] = String(properties["のし"] || "");
          legacyNoshi.表書 = String(properties["表書き"] || "");
          legacyNoshi.名前 = String(properties["名入れ"] || "");
        }
      }

      if (
        properties["手提げ袋"] &&
        hasLiveGiftId(bagVariantIds.length ? bagVariantIds : ids, giftQuantities)
      ) {
        var bagQuantitySuffix = giftQuantitySuffix(
          bagVariantIds.length ? bagVariantIds : ids,
          giftQuantities,
        );
        summaryParts.push(
          ["手提げ袋: " + properties["手提げ袋"], bagQuantitySuffix]
            .filter(Boolean)
            .join(" "),
        );
        if (!firstBag) firstBag = String(properties["手提げ袋"] || "");
      }

      if (
        properties["カード"] &&
        (!cardVariantId || giftQuantities[cardVariantId])
      ) {
        var cardQuantitySuffix = giftQuantitySuffix(
          cardVariantId ? [cardVariantId] : ids,
          giftQuantities,
        );
        summaryParts.push(
          ["シール: " + properties["カード"], cardQuantitySuffix]
            .filter(Boolean)
            .join(" "),
        );
        if (!firstCard) firstCard = String(properties["カード"] || "");
      }
    });

    return {
      ギフトオプション: summaryParts.join(" / "),
      熨斗: legacyNoshi.熨斗,
      熨斗の種類: legacyNoshi["熨斗の種類"],
      表書: legacyNoshi.表書,
      名前: legacyNoshi.名前,
      手提げ袋: firstBag,
      カード: firstCard,
    };
  }

  function collectGiftQuantities(cart) {
    var quantities = {};
    cart.items.forEach(function (item) {
      if (item.product_id !== GIFT_PRODUCT_ID) return;
      var variantId = String(item.variant_id || "");
      if (!variantId) return;
      quantities[variantId] = (quantities[variantId] || 0) + item.quantity;
    });
    return quantities;
  }

  function hasLiveGiftId(ids, giftQuantities) {
    return ids.some(function (id) {
      return !!giftQuantities[id];
    });
  }

  function giftQuantitySuffix(ids, giftQuantities) {
    var quantity = ids.reduce(function (total, id) {
      return total + (giftQuantities[id] || 0);
    }, 0);
    return quantity > 1 ? "（数量: " + quantity + "）" : "";
  }

  function haveAttributesChanged(current, next) {
    return GIFT_ATTRIBUTE_KEYS.some(function (key) {
      return String(current[key] || "") !== String(next[key] || "");
    });
  }

  function normalizeGiftId(value) {
    return normalizeIds([value])[0] || "";
  }

  function parseGiftIds(value) {
    if (!value) return [];
    if (Array.isArray(value)) return normalizeIds(value);

    var text = String(value).trim();
    if (!text) return [];

    try {
      var parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return normalizeIds(parsed);
    } catch (_) {}

    return normalizeIds(text.split(","));
  }

  function normalizeIds(values) {
    var seen = {};
    return values
      .map(function (value) {
        return String(value || "")
          .replace(/gid:\/\/shopify\/ProductVariant\//, "")
          .trim();
      })
      .filter(function (value) {
        if (!/^\d+$/.test(value) || seen[value]) return false;
        seen[value] = true;
        return true;
      });
  }

  function isCartPage() {
    return /^\/cart(?:\/|$)/.test(window.location.pathname);
  }

  function markGiftAccessoryRows(cart) {
    if (!isCartPage()) return;

    cart.items.forEach(function (item, index) {
      if (item.product_id !== GIFT_PRODUCT_ID) return;
      var oneBasedIndex = index + 1;
      [
        "CartItem-" + oneBasedIndex,
        "CartDrawer-Item-" + oneBasedIndex,
        "cart-notification-product-" + oneBasedIndex,
      ].forEach(function (id) {
        var row = document.getElementById(id);
        if (row) {
          row.classList.add("cart-item--gift-accessory");
          row.setAttribute("data-gift-accessory", "true");
          bindGiftAccessoryRemoveControls(row, item);
        }
      });
    });
  }

  function bindGiftAccessoryRemoveControls(row, item) {
    if (row.getAttribute("data-gift-remove-bound") === "true") return;
    row.setAttribute("data-gift-remove-bound", "true");
    row.addEventListener(
      "click",
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== "function") return;
        var removeControl = target.closest(
          'cart-remove-button, .cart-remove-button, a[href*="/cart/change"][href*="quantity=0"]',
        );
        if (!removeControl || !row.contains(removeControl)) return;
        rememberManualGiftChange({
          variantId: String(item.variant_id || ""),
          quantity: 0,
        });
      },
      true,
    );
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = "";
    if (typeof input === "string") url = input;
    else if (input && typeof input.url === "string") url = input.url;

    var isSyncCall = false;
    if (init && init.headers) {
      var h = init.headers;
      if (typeof h.get === "function") isSyncCall = h.get(SYNC_HEADER) === "1";
      else isSyncCall = h[SYNC_HEADER] === "1";
    }

    var manualGiftChange = !isSyncCall
      ? readManualGiftChange(url, init)
      : null;
    var result = origFetch.apply(this, arguments);
    if (
      !isSyncCall &&
      /\/cart\/(add|change|update)(?:\.js)?(\?|$)/.test(url)
    ) {
      result
        .then(function () {
          if (manualGiftChange) rememberManualGiftChange(manualGiftChange);
          scheduleSync({ reload: isCartPage() });
        })
        .catch(function () {});
    }
    return result;
  };

  function readManualGiftChange(url, init) {
    if (!/\/cart\/change(?:\.js)?(\?|$)/.test(url) || !lastCart) return null;
    var body = parseRequestBody(init && init.body);
    var line = parseInt(body.line, 10);
    var item = line ? lastCart.items[line - 1] : null;
    if (!item && body.id) {
      item = lastCart.items.find(function (cartItem) {
        return cartItem.key === body.id;
      });
    }
    if (!item || item.product_id !== GIFT_PRODUCT_ID) return null;

    var quantity = parseInt(body.quantity, 10);
    return {
      variantId: String(item.variant_id || ""),
      quantity: isNaN(quantity) ? item.quantity : quantity,
    };
  }

  function parseRequestBody(body) {
    if (!body) return {};

    if (typeof body === "string") {
      try {
        return JSON.parse(body);
      } catch (_) {
        var parsed = {};
        body.split("&").forEach(function (part) {
          var pair = part.split("=");
          if (pair.length !== 2) return;
          parsed[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
        });
        return parsed;
      }
    }

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var formData = {};
      body.forEach(function (value, key) {
        formData[key] = value;
      });
      return formData;
    }

    if (
      typeof URLSearchParams !== "undefined" &&
      body instanceof URLSearchParams
    ) {
      var params = {};
      body.forEach(function (value, key) {
        params[key] = value;
      });
      return params;
    }

    return {};
  }

  function rememberManualGiftChange(change) {
    if (!change.variantId) return;
    if (change.quantity === 0) {
      addRemovedGiftVariant(change.variantId);
      return;
    }
    forgetRemovedGiftVariants([change.variantId]);
  }

  function isRemovedGiftVariant(variantId) {
    return readRemovedGiftVariants().indexOf(String(variantId)) !== -1;
  }

  function forgetPrunedGiftVariants(targets) {
    var prunedIds = readRemovedGiftVariants().filter(function (id) {
      return !targets[id];
    });
    if (prunedIds.length > 0) forgetRemovedGiftVariants(prunedIds);
  }

  function addRemovedGiftVariant(variantId) {
    var ids = readRemovedGiftVariants();
    var id = String(variantId || "");
    if (!id || ids.indexOf(id) !== -1) return;
    ids.push(id);
    writeRemovedGiftVariants(ids);
  }

  function forgetRemovedGiftVariants(variantIds) {
    var removeMap = {};
    variantIds.forEach(function (id) {
      removeMap[String(id)] = true;
    });
    writeRemovedGiftVariants(
      readRemovedGiftVariants().filter(function (id) {
        return !removeMap[id];
      }),
    );
  }

  function readRemovedGiftVariants() {
    try {
      return normalizeIds(
        JSON.parse(sessionStorage.getItem(REMOVED_STORAGE_KEY) || "[]"),
      );
    } catch (_) {
      return [];
    }
  }

  function writeRemovedGiftVariants(ids) {
    try {
      var normalized = normalizeIds(ids);
      if (normalized.length === 0) {
        sessionStorage.removeItem(REMOVED_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(REMOVED_STORAGE_KEY, JSON.stringify(normalized));
    } catch (_) {}
  }

  // カート側の編集UI（cart-gift-editor.js）から同期を制御するための入口。
  // 直前に付け直した付属品が「削除済み」として弾かれないよう forget を先に呼ぶ。
  window.SuecoGiftSync = {
    GIFT_PRODUCT_ID: GIFT_PRODUCT_ID,
    forget: function (variantIds) {
      forgetRemovedGiftVariants(normalizeIds(variantIds || []));
    },
    syncNow: function (options) {
      return runSync(options || {});
    },
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      if (isCartPage()) scheduleSync({ reload: true });
    });
  } else if (isCartPage()) {
    scheduleSync({ reload: true });
  }
})();
